import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDiscounts as useModalDiscounts } from '@components/layout/Modals/BillingSettingsModal/hooks/useDiscounts'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@store/index'
import { getAllDiscount } from '@store/actions/discount.action'
import { SortDirection } from '@constants/Common/sort-direction.constant'
import { SettingStatus } from '@dataTypes/settingStatus.type'

const DISCOUNTS_LOADING_CONTAINER = '.settings-accounting-discounts'

type DiscountFilterMap = Record<string, string[]>

export const useDiscounts = (
  searchQuery: string,
  filters: DiscountFilterMap,
  ...args: Parameters<typeof useModalDiscounts>
): ReturnType<typeof useModalDiscounts> & {
  discounts: ReturnType<typeof useModalDiscounts>['discounts']
  discountCount: number
  currentPage: number
  pageSize: number
  listLoading: boolean
  handlePageChange: (page: number) => void
  handlePageSizeChange: (nextPageSize: number) => void
  handleTableSortChange: (sort: {
    key: string
    direction: SortDirection
  }) => void
} => {
  const [showToast, showLoading, hideLoading] = args
  const dispatch = useDispatch<AppDispatch>()
  const selectedClinic = useSelector(
    (state: RootState) => state.clinic.selectedClinic,
  )
  const { count } = useSelector((state: RootState) => state.discount)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [sort, setSort] = useState<string>('description:ASC')
  const [listLoading, setListLoading] = useState(true)
  const initialLoadRef = useRef(true)

  const showPageLoading = useCallback<
    NonNullable<Parameters<typeof useModalDiscounts>[1]>
  >(
    (options) => {
      showLoading?.({ ...options, container: DISCOUNTS_LOADING_CONTAINER })
    },
    [showLoading],
  )

  const hidePageLoading = useCallback<
    NonNullable<Parameters<typeof useModalDiscounts>[2]>
  >(() => {
    hideLoading?.(DISCOUNTS_LOADING_CONTAINER)
  }, [hideLoading])

  const statusFilters = useMemo(
    () =>
      filters.status?.filter(
        (value): value is SettingStatus =>
          value === SettingStatus.Enabled || value === SettingStatus.Disabled,
      ),
    [filters.status],
  )

  const buildListQuery = useCallback(
    (page: number) => {
      const q = searchQuery.trim()
      return {
        ...(q ? { search: q } : {}),
        ...(statusFilters?.length
          ? {
              status:
                statusFilters.length === 1 ? statusFilters[0] : statusFilters,
            }
          : {}),
        sort,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      }
    },
    [pageSize, searchQuery, sort, statusFilters],
  )

  const getListFilters = useCallback(
    () => buildListQuery(currentPage),
    [buildListQuery, currentPage],
  )

  const modalHooks = useModalDiscounts(
    showToast,
    showPageLoading,
    hidePageLoading,
    getListFilters,
  )

  const { reset } = modalHooks

  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, filters.status])

  useEffect(() => {
    initialLoadRef.current = true
    setListLoading(true)
    setCurrentPage(1)
    reset()
    dispatch(getAllDiscount(buildListQuery(1)))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- clinic-only; buildListQuery is latest
  }, [dispatch, reset, selectedClinic?.clinic_id])

  useEffect(() => {
    const isInitialLoad = initialLoadRef.current
    if (isInitialLoad) setListLoading(true)
    void dispatch(getAllDiscount(getListFilters())).finally(() => {
      if (!isInitialLoad) return
      initialLoadRef.current = false
      setListLoading(false)
    })
  }, [dispatch, getListFilters])

  const handlePageChange = useCallback((page: number): void => {
    setCurrentPage(page)
  }, [])

  const handlePageSizeChange = useCallback((nextPageSize: number): void => {
    setPageSize(nextPageSize)
    setCurrentPage(1)
  }, [])

  const handleTableSortChange = useCallback(
    ({ key, direction }: { key: string; direction: SortDirection }): void => {
      if (
        key === 'status' ||
        key === 'description' ||
        key === 'discount_percentage'
      ) {
        setSort(`${key}:${direction}`)
        setCurrentPage(1)
      }
    },
    [],
  )

  const displayDiscounts = modalHooks.getDisplayData('', {})

  return {
    ...modalHooks,
    discounts: displayDiscounts,
    discountCount: count,
    currentPage,
    pageSize,
    listLoading,
    handlePageChange,
    handlePageSizeChange,
    handleTableSortChange,
  }
}

export default useDiscounts
